import { useCallback, useState } from "react";

import { TypeInteractionApi } from "data";
import { TypeInteraction, TypeInteractionResult } from "domain/entities/type-interaction";
import GetAllUseCase from "domain/use-cases/type-interaction/GetAllUseCase";
import CreateUseCase from "domain/use-cases/type-interaction/CreateUseCase";
import UpdateUseCase from "domain/use-cases/type-interaction/UpdateUseCase";
import DeleteUseCase from "domain/use-cases/type-interaction/DeleteUseCase";
import { useToken } from "presentation/view-models/hooks";
import { TokenPromise } from "presentation/utils/interfaces/types";
import { IRequestResult2, RequestType } from "presentation/utils/interfaces/IRequestResult2";

// Retorna una promesa para obtener de la API todas los efectos de medicamentos de interacción registrados.
const _getAllTypeInteraction = (token: TokenPromise) => {
    const typeInteractionRepository = new TypeInteractionApi();
    const getAllUseCase = new GetAllUseCase(typeInteractionRepository);

    return getAllUseCase.getAllTypeInteraction(token);
};

// Retorna una promesa para agregar un efecto desde la API.
const _createTypeInteraction = (typeInteraction: TypeInteraction, token: TokenPromise) => {
    const typeInteractionRepository = new TypeInteractionApi();
    const createUseCase = new CreateUseCase(typeInteractionRepository);

    return createUseCase.createTypeInteraction(typeInteraction, token);
};

// Retorna una promesa para editar un efecto desde la API.
const _editTypeInteraction = (typeInteractionId: number, typeInteraction: TypeInteraction, token: TokenPromise) => {
    const typeInteractionRepository = new TypeInteractionApi();
    const updateUseCase = new UpdateUseCase(typeInteractionRepository);

    return updateUseCase.updateTypeInteraction(typeInteractionId, typeInteraction, token);
};

// Retorna una promesa para eliminar desde la API una severidad por su ID.
const _deleteTypeInteraction = (typeInteractionId: number, token: TokenPromise) => {
    const typeInteractionRepository = new TypeInteractionApi();
    const deleteUseCase = new DeleteUseCase(typeInteractionRepository);

    return deleteUseCase.deleteTypeInteraction(typeInteractionId, token);
};

// Hook usado para gestionar el CRUD de efectos.
export const useTypeInteraction = () => {
    // Estado del hook.
    const [state, setState] = useState<IRequestResult2>({
        dataLoading: false,
        loading: false,
        reqType: undefined,
        reqCompleted: undefined,
        error: undefined,
    });
    const [data, setData] = useState<TypeInteractionResult[]>([]);
    const { getTokens } = useToken();

    const isUpdateStateOk = useCallback((error?: Error | null): boolean => {
        if (error) {
            setState((prev) => ({ ...prev, loading: false, reqCompleted: true, error: error.message }));
            return false;
        } else {
            setState((prev) => ({ ...prev, loading: false, reqCompleted: true, error: undefined }));
            return true;
        }
    }, []);

    // Obtiene de la API todas las efectos registrados.
    const getAllTypeInteraction = useCallback(() => {
        setState({ dataLoading: true, loading: false });
        _getAllTypeInteraction(getTokens()).then(([res, error]) => {
            setState({
                reqType: RequestType.GET,
                dataLoading: false,
                loading: false,
                error: error?.message,
            });
            setData(res ?? []);
        });
    }, [getTokens]);

    // Guarda un efecto desde API
    const saveTypeInteraction = useCallback((typeInteraction: TypeInteractionResult) => {
        setState((prev) => ({ ...prev, reqType: RequestType.POST, loading: true, reqCompleted: false }));
        _createTypeInteraction(typeInteraction, getTokens()).then(([res, error]) => {
            if (isUpdateStateOk(error)) {
                typeInteraction.id = res?.data?.id ?? 0;
                setData((prev) => ([...prev ?? [], typeInteraction]));
            }
        });
    }, [getTokens, isUpdateStateOk]);

    // Edita un efecto desde API
    const editTypeInteraction = useCallback((typeInteraction: TypeInteractionResult) => {
        setState((prev) => ({ ...prev, reqType: RequestType.PUT, loading: true, reqCompleted: false }));
        _editTypeInteraction(typeInteraction.id, typeInteraction, getTokens()).then(([_, error]) => {
            if (isUpdateStateOk(error)) {
                setData((prev) => (prev?.map(item => item.id === typeInteraction.id ? typeInteraction : item)));
            }
        });
    }, [getTokens, isUpdateStateOk]);

    // Elimina un efecto del estado
    const deleteTypeInteraction = useCallback((typeInteractionId: number) => {
        setState((prev) => ({ ...prev, reqType: RequestType.DELETE, loading: true, reqCompleted: false }));
        _deleteTypeInteraction(typeInteractionId, getTokens()).then(([_, error]) => {
            if (isUpdateStateOk(error)) {
                setData((prev) => (prev?.filter(item => item.id !== typeInteractionId)));
            }
        });
    }, [getTokens, isUpdateStateOk]);

    return { getAllTypeInteraction, saveTypeInteraction, editTypeInteraction, deleteTypeInteraction, state, data };
};